import { useState } from "react";
import { X, Upload, FileText, Loader2 } from "lucide-react";
import api from "../../../services/api";

var DOC_TYPE_OPTIONS = [
  { value: "boleto", label: "Boleto" },
  { value: "apolice", label: "Apolice de Seguro" },
  { value: "consorcio_doc", label: "Documento de Consórcio" },
];

export default function UploadDocumentModal({ clientId, consortiums, policies, onClose, onSuccess }) {
  var formState = useState({
    document_type: "boleto",
    product_id: "",
    product_description: "",
    value: "",
    due_date: "",
  });
  var form = formState[0];
  var setForm = formState[1];
  var fileState = useState(null);
  var file = fileState[0];
  var setFile = fileState[1];
  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var errorState = useState("");
  var error = errorState[0];
  var setError = errorState[1];

  var products = [];
  if (consortiums) {
    for (var i = 0; i < consortiums.length; i++) {
      var c = consortiums[i];
      products.push({ id: c.id, label: "Consorcio - " + (c.grupo ? "G" + c.grupo : "") + (c.cota ? " C" + c.cota : "") + " " + (c.administrator || ""), type: "consorcio" });
    }
  }
  if (policies) {
    for (var j = 0; j < policies.length; j++) {
      var p = policies[j];
      products.push({ id: p.id, label: "Seguro - " + (p.insurer_name || "") + " " + (p.insurance_type || "") + " " + (p.policy_number || ""), type: "seguro" });
    }
  }

  function handleChange(field, value) {
    setForm(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      if (field === "product_id" && value) {
        var found = products.find(function(pr) { return pr.id === value; });
        if (found) next.product_description = found.label;
      }
      return next;
    });
  }

  function handleFile(e) {
    var f = e.target.files[0];
    if (f && f.size > 20 * 1024 * 1024) {
      setError("Arquivo muito grande (max 20MB)");
      return;
    }
    setFile(f);
    setError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) { setError("Selecione um arquivo"); return; }
    setLoading(true);
    setError("");

    var fd = new FormData();
    fd.append("file", file);
    fd.append("client_id", clientId);
    fd.append("document_type", form.document_type);
    fd.append("product_id", form.product_id);
    fd.append("product_description", form.product_description);
    fd.append("value", form.value || "0");
    fd.append("due_date", form.due_date);

    try {
      await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSuccess();
    } catch (err) {
      var msg = err.response && err.response.data && err.response.data.detail;
      setError(msg || "Erro ao enviar documento");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="upload-doc-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#0A2F3D]">Enviar Documento</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="close-upload-modal">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg" data-testid="upload-error">{error}</p>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Documento *</label>
            <select
              value={form.document_type}
              onChange={function(e) { handleChange("document_type", e.target.value); }}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
              data-testid="doc-type-select"
            >
              {DOC_TYPE_OPTIONS.map(function(opt) { return <option key={opt.value} value={opt.value}>{opt.label}</option>; })}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Produto Vinculado</label>
            <select
              value={form.product_id}
              onChange={function(e) { handleChange("product_id", e.target.value); }}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
              data-testid="doc-product-select"
            >
              <option value="">Nenhum (geral)</option>
              {products.map(function(pr) { return <option key={pr.id} value={pr.id}>{pr.label}</option>; })}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.value}
                onChange={function(e) { handleChange("value", e.target.value); }}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
                placeholder="0,00"
                data-testid="doc-value-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vencimento</label>
              <input
                type="date"
                value={form.due_date}
                onChange={function(e) { handleChange("due_date", e.target.value); }}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
                data-testid="doc-due-date-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Arquivo *</label>
            <div className="relative">
              <input
                type="file"
                onChange={handleFile}
                className="hidden"
                id="doc-file-input"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                data-testid="doc-file-input"
              />
              <label
                htmlFor="doc-file-input"
                className="flex items-center justify-center gap-2 w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#3B82F6] hover:bg-[#3B82F6]/5 transition-colors"
              >
                {file ? (
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#3B82F6]" />
                    <span className="text-sm text-gray-700 truncate max-w-[300px]">{file.name}</span>
                    <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(0)} KB)</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Upload className="w-5 h-5" />
                    <span className="text-sm">Clique para selecionar</span>
                  </div>
                )}
              </label>
            </div>
            <p className="text-xs text-gray-400 mt-1">PDF, imagens, Word ou Excel (max 20MB)</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors" data-testid="cancel-upload-btn">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#3B82F6] hover:bg-[#2563EB] rounded-xl transition-colors disabled:opacity-50"
              data-testid="submit-upload-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {loading ? "Enviando..." : "Enviar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
